/**
 * DataTable Manager with Alpine.js Integration.
 *
 * This module provides a comprehensive DataTable management system that integrates
 * with Alpine.js for reactive state management. It supports server-side processing,
 * filtering, pagination, sorting, and various UI components for data manipulation.
 *
 * Key Features:
 * - Server-side DataTable processing
 * - Advanced filtering (golbal search, columns wise filters, faceted, date ranges, etc.)
 * - Pagination and sorting
 * - Column visibility controls
 * - CSV export functionality
 * - Reactive Alpine.js integration
 *
 * @author Perfect Golf Event
 * @version 1.0.0
 */

/**
 * Maps operator values to their human-readable labels.
 * Shared between the search-filter and applied-filter-badge components.
 */
const OPERATOR_LABELS = {
  eq: "=",
  neq: "!=",
  lt: "<",
  gt: ">",
  lte: "<=",
  gte: ">=",
  like: "contains",
  between: "between",
  isnull: "is empty",
  notnull: "is not empty",
};

/**
 * Main DataTable Manager class that handles DataTable instances and their associated
 * Alpine.js stores. Supports multiple DataTable instances on the same page through
 * a store-based architecture.
 */
class DataTableManager {
  /**
   * Static map to store DataTableManager instances by store name
   */
  static _byStore = new Map();

  /**
   * Reference to Alpine.js instance
   */
  static Alpine = null;

  /**
   * Sets the Alpine.js instance for the DataTableManager
   */
  static setAlpine(alpine) {
    if (!this.Alpine) this.Alpine = alpine;
  }

  /**
   * Creates or retrieves a DataTableManager instance bound to a specific Alpine store
   */
  static bind(storeName) {
    const Alpine = this.Alpine || window.Alpine;

    if (!Alpine) throw new Error("Alpine not set on DataTableManager");

    if (!Alpine.store(storeName)) {
      Alpine.store(storeName, DataTableManager.initAlpineStore());
    }

    let dt = this._byStore.get(storeName);
    if (!dt) {
      dt = new DataTableManager();

      dt.Alpine = Alpine;
      dt.storeName = storeName;
      dt.store = Alpine.store(storeName);

      this._byStore.set(storeName, dt);
    }

    return dt;
  }

  /**
   * Retrieves an existing DataTableManager instance by store name
   */
  static use(storeName) {
    return this._byStore.get(storeName);
  }

  /**
   * Initializes the Alpine.js store with default state and methods for DataTable management
   */
  static initAlpineStore() {
    return {
      table: null,
      filters: [],
      globalSearch: "",
      totalPages: 1,
      currentPage: 1,
      totalRecords: 0,
      sortBy: 0,
      limit: 10,
      sortOrder: "asc",

      /**
       * Computed property to check if any filters are applied
       */
      // prettier-ignore
      get hasFilters() {
        return this.globalSearch.length > 0 || this.filters.length > 0;
      },

      /**
       * Adds or updates filters and triggers table reload
       */
      addFilters(filters) {
        const filteredCol = new Set(filters.map((c) => c.column));

        this.filters = [
          ...(this.filters || []).filter((f) => !filteredCol.has(f.column)),
          ...filters,
        ];

        this.currentPage = 1;
        this.reload();
      },

      /**
       * Removes a specific filter by column
       */
      removeFilter(col) {
        this.filters = this.filters.filter(
          (filter) => String(filter.column) !== String(col.column)
        );
        this.currentPage = 1;
        this.reload();
      },

      /**
       * Removes multiple filters by column names
       */
      removeFilters(filters) {
        this.filters = this.filters.filter(
          (filter) => !filters.includes(filter.column)
        );
        this.currentPage = 1;
        this.reload();
      },

      // Reset filters to default state.
      resetFilters() {
        this.filters = [];
        this.limit = 10;
        this.sortBy = 0;
        this.sortOrder = "asc";
        this.currentPage = 1;
        this.globalSearch = "";

        this.reload();
      },

      // Handle pagination.
      setPage(page) {
        if (page > 0 && page <= this.totalPages) {
          this.currentPage = page;
          this.reload();
        }
      },

      /**
       * Sets the page size (number of records per page)
       */
      setPageSize(value) {
        this.limit = value;
        this.currentPage = 1;
        this.reload();
      },

      /**
       * Update pagination info.
       */
      updatePagination(totalRecords) {
        this.totalRecords = totalRecords;
        this.totalPages = Math.ceil(totalRecords / this.limit);
        this.currentPage = Math.min(this.currentPage, this.totalPages);
      },

      /**
       * Prepares filter data for server-side processing.
       */
      prepareFilters(dtFilters) {
        this.sortBy = dtFilters.order?.[0]?.column ?? this.sortBy ?? 0;
        this.sortOrder = dtFilters.order?.[0]?.dir ?? this.sortOrder ?? "asc";

        return {
          ...{
            start: (this.currentPage - 1) * (this.limit || 10),
            sortBy: this.sortBy,
            sortOrder: this.sortOrder,
          },
          limit: this.limit,
          filters: this.filters,
          search: this.globalSearch,
        };
      },

      /**
       * Reload the DataTable.
       */
      reload() {
        this.table?.ajax?.reload(null, false);
      },
    };
  }

  /**
   * Initializes a DataTable instance with server-side processing
   */
  initDataTable(tableSelector, requestedApiUrl) {
    const dtStore = this.store;

    /**
     * Destroy existing DataTable instance for safety (HMR / re-navigation)
     */
    if ($.fn.DataTable.isDataTable(tableSelector)) {
      try {
        $(tableSelector).DataTable().destroy(true);
      } catch {}
    }

    /**
     * Initialize DataTable with server-side processing.
     */
    dtStore.table = $(tableSelector).DataTable({
      info: false,
      paging: false,
      searching: false,
      serverSide: true,
      pageLength: dtStore.limit || 10,
      bProcessing: true,
      columnDefs: [{ targets: "no-sort", orderable: false }],
      ajax: {
        url: requestedApiUrl,
        type: "get",
        
        /**
         * Transforms request data before sending to server.
         */
        data: (data) => {
          Object.assign(data, dtStore.prepareFilters(data));

          delete data.columns;
          delete data.length;
          delete data.order;
        },

        dataSrc: (json) => {
          // Extract total records count.
          const total =
            typeof json.recordsFiltered === "number"
              ? json.recordsFiltered
              : json.recordsTotal ?? 0;

          // Update pagination state.
          dtStore.updatePagination(total);

          return json.data || [];
        },
      },
      
      /**
       * Callback executed after each table draw.
       */
      drawCallback: (settings) => {
        dtStore.updatePagination(settings?._iRecordsTotal ?? 0);
      },
    });

    // Expandable row feature (inject expander button + click handler) is
    // temporarily disabled. See git history for the full implementation.
  }

  /**
   * Initializes advanced search filters component for DataTable.
   */
  initDtSearchFilters({ columns, allowedFormats }) {
    const dtStore = this.store;

    return {
      open: false,

      state: columns.map((c) => ({
        title: c.title,
        format: c.format || "string",
        column: c.column,
        operator: "eq", // default operator
        value: "",
        _open: false, // dropdown state for operator selection.
      })),

      /**
       * Available filter operators with their labels and allowed data types.
       */
      //prettier-ignore
      operatorOptions: [
        {
          value: "eq",
          label: "=",
          allowedFor: ["date", "string", "number", "boolean", "currency"],
        },
        {
          value: "neq",
          label: "!=",
          allowedFor: ["date", "string", "number", "boolean", "currency"],
        },
        {
          value: "lt",
          label: "<",
          allowedFor: ["date", "number", "currency"],
        },
        {
          value: "gt",
          label: ">",
          allowedFor: ["date", "number", "currency"],
        },
        {
          value: "lte",
          label: "<=",
          allowedFor: ["date", "number", "currency"],
        },
        {
          value: "gte",
          label: ">=",
          allowedFor: ["date", "number", "currency"],
        },
        {
          value: "like",
          label: "contains",
          allowedFor: ["string", "phone"],
        },
        {
          value: "between",
          label: "between",
          allowedFor: ["date", "number", "currency"],
        },
        {
          value: "isnull",
          label: "is empty",
          allowedFor: ["date", "string", "number", "boolean", "phone"],
        },
        {
          value: "notnull",
          label: "is not empty",
          allowedFor: ["date", "string", "number", "boolean", "phone"],
        },
      ],

      /**
       * Getter/setter for global search term
       */
      get globalSearch() {
        return dtStore.globalSearch ?? "";
      },

      /**
       * Sets global search and triggers table reload.
       */
      set globalSearch(v) {
        dtStore.globalSearch = v;
        dtStore.currentPage = 1;
        dtStore.reload();
      },

      /**
       * Re-evaluates search filter state based on current applied filters.
       */
      reEvaluateSearchFilters() {
        this.state = this.state.map((c) => {
          return dtStore.filters.find((f) => f.column === c.column)
            ? c
            : { ...c, operator: "eq", value: "", startVal: "", endVal: "" };
        });
      },

      /**
       * Gets allowed operators for a specific column based on its data format.
       */
      opsAllowed(col) {
        return this.operatorOptions.filter(
          (op) =>
            op.allowedFor?.includes(
              allowedFormats.includes(col.format) ? col.format : "string"
            ) || op.format === "string"
        );
      },

      /**
       * Gets the display label for an operator.
       */
      operatorLabel(op) {
        return OPERATOR_LABELS[op] ?? op;
      },

      /**
       * Sets the operator for a column and resets its values
       */
      setOperator(col, op) {
        col.operator = op;
        col.value = "";
        col.startVal = "";
        col.endVal = "";
        col._open = false;
      },

      /**
       * Handles input events with format-specific sanitization.
       */
      onInput(col, key, $event) {
        const raw = $event?.target?.value ?? "";
        const sanitized = this._sanitizeByFormat(col.format, raw);
        if ($event && $event.target) $event.target.value = sanitized;

        col[key] = sanitized;
      },

      /**
       * Clears filter for a specific column.
       */
      clearColumn(col) {
        if (dtStore.filters.find((f) => f.column === col.column)) {
          dtStore.removeFilter(col);
        }

        col.value = "";
        col.startVal = "";
        col.endVal = "";
        col.operator = "eq";
      },

      /**
       * Clears all column filters.
       */
      clearAll() {
        this.state.forEach((c) => {
          c.value = "";
          c.startVal = "";
          c.endVal = "";
          c.operator = "eq";
        });

        if (dtStore?.filters) {
          // removeFilters already calls reload() internally
          dtStore.removeFilters(this.state.map((filter) => filter.column));
        }
      },

      /**
       * Applies current filter state to the DataTable.
       */
      apply() {
        const filters = this.state
          .filter((c) => this.isValueExists(c))
          .map((c) => ({
            column: c.column,
            operator: c.operator,
            value:
              c.operator === "between"
                ? [this.formatValue(c.startVal), this.formatValue(c.endVal)]
                : this.formatValue(c.value),
          }));

        dtStore.globalSearch = "";
        dtStore.addFilters(filters);
        this.open = false;
      },

      /**
       * Checks if a column has a valid filter value.
       */
      isValueExists(col) {
        return (
          ["notnull", "isnull"].includes(col.operator) ||
          (col.operator === "between" &&
            (String(col.startVal ?? "").trim() !== "" ||
              String(col.endVal ?? "").trim() !== "")) ||
          String(col.value ?? "").trim() !== ""
        );
      },

      /**
       * Formats a value for server submission (converts numeric strings to numbers).
       */
      formatValue(value) {
        return isFinite(value) && value !== "" ? Number(value) : value;
      },

      /**
       * Sanitizes input based on data format.
       */
      _sanitizeByFormat(format, value) {
        const str = value === null || value === undefined ? "" : String(value);

        switch (format) {
          case "number":
            return this._sanitizeNumber(str);
          case "currency":
            return this._sanitizeCurrency(str);
          case "phone":
            return this._sanitizePhone(str);
          case "date":
            return this._sanitizeDate(str);
          default:
            return str;
        }
      },

      /**
       * Sanitizes numeric input (digits only).
       */
      _sanitizeNumber(value) {
        return !value ? "" : value.replace(/[^0-9]/g, "");
      },

      /**
       * Sanitizes currency input (digits and decimal point)
       */
      _sanitizeCurrency(value) {
        if (!value) return "";

        let v = String(value).replace(/[^0-9.]/g, "");

        // Only allow one decimal point.
        const parts = v.split(".");
        if (parts.length > 2) {
          v = parts[0] + "." + parts.slice(1).join("");
        }

        // Limit to 2 decimal places.
        const cleanParts = v.split(".");
        if (cleanParts.length === 2) {
          v = cleanParts[0] + "." + cleanParts[1].slice(0, 2);
        }

        // Remove leading zeros unless "0" or "0.xx"
        v = v.replace(/^0+(\d)/, "$1");

        return v;
      },

      /**
       * Sanitizes phone number input (digits only, max 10 digits).
       */
      _sanitizePhone(value) {
        return !value ? "" : value.replace(/[^0-9]/g, "").substring(0, 10);
      },

      /**
       * Sanitizes date input with auto-formatting (MM/DD/YYYY).
       */
      _sanitizeDate(value) {
        if (!value) return "";

        // Keep digits only, cap at 8 (MMDDYYYY).
        const digits = String(value).replace(/\D/g, "").slice(0, 8);

        // Auto-insert slashes as the user types.
        let out = digits;
        if (digits.length > 4) {
          out = `${digits.slice(0, 2)}/${digits.slice(2, 4)}/${digits.slice(
            4
          )}`;
        } else if (digits.length > 2) {
          out = `${digits.slice(0, 2)}/${digits.slice(2)}`;
        }

        // If full length, validate real calendar date.
        if (out.length === 10) {
          const mm = parseInt(out.slice(0, 2), 10);
          const dd = parseInt(out.slice(3, 5), 10);
          const yyyy = parseInt(out.slice(6, 10), 10);

          // Month validation (1-12).
          if (mm < 1 || mm > 12) return "";

          // Day validation (1-daysInMonth, handles leap years).
          const daysInMonth = new Date(yyyy, mm, 0).getDate();

          if (dd < 1 || dd > daysInMonth) return "";
        }

        return out;
      },
    };
  }

  /**
   * Initializes faceted filters component (dropdown with predefined options)
   */
  initDtFacetedFilters({ storeName, options, column, selected }) {
    const dtStore = this.store;

    return {
      open: false,
      column,
      options,
      storeName,
      selected,

      /**
       * Checks if a value is currently selected
       */
      get hasSelected() {
        return String(this.selected ?? "").trim() !== "";
      },

      /**
       * Gets the display text for the filter badge
       */
      get badgeText() {
        if (!this.hasSelected) return "";
        return (
          this.options.find((o) => String(o.value) === String(this.selected))
            ?.label ?? "1 selected"
        );
      },

      /**
       * Checks if a specific option is selected
       */
      isOptSelected(optionVal) {
        return String(this.selected) === String(optionVal);
      },

      /**
       * Toggles selection of an option
       */
      // prettier-ignore
      toggle(selectOpt) {
        this.selected = String(this.selected) === String(selectOpt) ? "" : String(selectOpt);
        this.apply();
      },

      /**
       * Clears the current selection
       */
      clear() {
        this.open = false;
        this.selected = "";
        dtStore.removeFilter({ column: String(column) });
      },

      /**
       * Applies the current selection as a filter
       */
      apply() {
        if (this.hasSelected) {
          dtStore.addFilters([
            {
              column: String(column),
              operator: "eq",
              value: String(this.selected),
            },
          ]);
        } else {
          dtStore.removeFilter({ column: String(column) });
        }
      },
    };
  }

  /**
   * Initializes column visibility filter component.
   */
  initDtVisibleFilter({ options = [], selectedValues = [] }) {
    const dtStore = this.store;

    return {
      open: false,
      hidden: Array.isArray(selectedValues) ? [...selectedValues] : [],
      columns: (options || []).map((o, i) => ({
        index: i,
        title: o.title ?? `Column ${i + 1}`,
      })),

      get visibleCount() {
        return this.columns.length - this.hidden.length;
      },

      isHidden(i) {
        return this.hidden.includes(i);
      },

      toggle(i) {
        const isHidden = this.isHidden(i);

        this.hidden = isHidden
          ? this.hidden.filter((x) => x !== i)
          : [...this.hidden, i];

        this.apply();
      },

      apply() {
        // Hide/show specified columns in the DataTable
        if (dtStore?.table) {
          dtStore.table.columns()?.every((index) => {
            dtStore.table.column(index).visible(!this.hidden.includes(index));
          });
        }
      },
    };
  }

  /**
   * Initializes CSV export functionality
   */
  initDtExportCsvFilters({ url, options }) {
    const dtStore = this.store;

    return {
      open: false,
      url,
      skipped: [],
      options,

      /*
       * Checks if a column is skipped in export.
       */
      isSkipped(i) {
        return (this.skipped || []).includes(i);
      },

      /*
       * Toggles whether a column is included in export.
       */
      toggle(index) {
        this.skipped = this.skipped.includes(index)
          ? this.skipped.filter((i) => i !== index)
          : [...this.skipped, index];
      },

      /*
       * Initiates CSV export with current filters and settings.
       */
      exportCsvNow() {
        this.open = false;

        const params = new URLSearchParams();

        params.set("search", dtStore.globalSearch ?? "");
        params.set("sortBy", dtStore.sortBy ?? 0);
        params.set("sortOrder", dtStore.sortOrder ?? "asc");
        params.set("skippedColumns", (this.skipped || []).join(","));

        dtStore.filters.forEach((f, i) => {
          params.append(`filters[${i}][column]`, f.column);
          params.append(`filters[${i}][operator]`, f.operator);

          if (Array.isArray(f.value)) {
            f.value.forEach((v) =>
              params.append(`filters[${i}][value][]`, v ?? "")
            );
          } else if (f.value !== null && f.value !== undefined) {
            params.append(`filters[${i}][value]`, String(f.value));
          } else {
            // Send empty string for nullish values (isnull/notnull operators)
            params.append(`filters[${i}][value]`, "");
          }
        });

        // Open export URL in new tab
        window.open(`${url}?${params.toString()}`, "_blank");
      },
    };
  }

  /**
   * Initializes pagination controls component.
   * currentPage and totalPages are delegated to the store so the component
   * stays in sync when filters are reset or another component changes the page.
   */
  initDtPaginationFilters({}) {
    const dtStore = this.store;

    return {
      get currentPage() { return dtStore.currentPage; },
      get totalPages()  { return dtStore.totalPages; },

      /**
       * Handles pagination actions
       */
      setPage(action) {
        const page = {
          first:    1,
          last:     dtStore.totalPages,
          next:     dtStore.currentPage + 1,
          previous: dtStore.currentPage - 1,
        }[action];

        if (page !== undefined) dtStore.setPage(page);
      },

      /**
       * Checks if previous button should be disabled
       */
      get disabledPrevious() {
        return dtStore.currentPage <= 1;
      },

      /**
       * Checks if next button should be disabled
       */
      get disabledNext() {
        return dtStore.currentPage >= dtStore.totalPages;
      },
    };
  }

  /**
   * Initializes page length (records per page) selector component.
   */
  initDtPageLengthFilters({ options }) {
    const dtStore = this.store;

    return {
      open: false,
      limit: 10,
      options: options,

      get totalRecords() {
        return dtStore.totalRecords;
      },

      setSize(length) {
        this.limit = length;
        dtStore.setPageSize(length);
      },
    };
  }

  /**
   * Initializes component for rendering applied filters as badges
   */
  initDtRenderAppliedFilters({ options }) {
    const dtStore = this.store;

    return {
      /**
       * Gets currently applied filters with their display information.
       */
      selectedFilters() {
        const searchables = new Map(options.map((o) => [o.column, o]));
        const filters = dtStore.filters;

        return filters
          .map((f) => ({ ...f, title: searchables.get(f.column)?.title }))
          .filter((f) => f.title);
      },

      op(op) {
        return OPERATOR_LABELS[op] ?? op;
      },

      /**
       * Formats filter values for display.
       */
      val(f) {
        if (f.operator === "isnull" || f.operator === "notnull") return "";
        if (f.operator === "between" && Array.isArray(f.value)) {
          const [a, b] = f.value;
          return `${a ?? "…"} – ${b ?? "…"}`;
        }

        return Array.isArray(f.value)
          ? f.value.join(", ")
          : String(f.value ?? "");
      },

      /**
       * Creates a complete filter label for display.
       */
      label(f) {
        const title = f.title;
        const operator = this.op(f.operator);
        const value = this.val(f);

        return value ? `${title} ${operator} ${value}` : `${title} ${operator}`;
      },

      /**
       * Removes a specific filter.
       */
      remove(col) {
        dtStore.removeFilter(col);
      },
    };
  }
}

/**
 * Public initialization function for Alpine DataTable integration
 *
 * This function sets up the DataTable with Alpine.js integration and registers
 * all necessary Alpine components for filtering, pagination, and data management.
 *
 * @param {Object} Alpine - Alpine.js instance
 * @param {Object} config - Configuration object
 * @param {string} config.tableSelector - CSS selector for the table element
 * @param {string} config.requestedApiUrl - API endpoint URL for data fetching
 * @param {string} [config.storeName='dataTableFilters'] - Alpine store name
 *
 * @example
 * // Initialize DataTable with Alpine.js
 * initAlpineDataTable(Alpine, {
 *   tableSelector: '#my-table',
 *   requestedApiUrl: '/api/data',
 *   storeName: 'myTableStore'
 * });
 */
// Public init (idempotent registration; multi-table friendly)
export function initAlpineDataTable(
  Alpine,
  { tableSelector, requestedApiUrl, storeName = "dataTableFilters" }
) {
  // Set Alpine instance on DataTableManager
  DataTableManager.setAlpine(Alpine);

  // Get or create DataTableManager instance for this store
  const dtFactory = DataTableManager.bind(storeName);

  // Register Alpine components once globally (idempotent)
  // prettier-ignore
  if (!window.__dtPluginInstalled) {
    window.__dtPluginInstalled = true;

    // Helper function to get or create manager instance
    const useMgr = (sn) => DataTableManager.use(sn) ?? DataTableManager.bind(sn);

    // Register all Alpine.js data components
    Alpine.data("dtSearchFilters", (args) => useMgr(args.storeName).initDtSearchFilters(args));
    Alpine.data("dtFacetedFilters", (args) => useMgr(args.storeName).initDtFacetedFilters(args));
    Alpine.data("dtVisibleFilter", (args) => useMgr(args.storeName).initDtVisibleFilter(args));
    Alpine.data("dtExportCsvFilters", (args) => useMgr(args.storeName).initDtExportCsvFilters(args));
    Alpine.data("dtPaginationFilters", (args) => useMgr(args.storeName).initDtPaginationFilters(args));
    Alpine.data("dtPageLengthFilters", (args) => useMgr(args.storeName).initDtPageLengthFilters(args));
    Alpine.data("dtRenderAppliedFilters", (args) => useMgr(args.storeName).initDtRenderAppliedFilters(args));
  }

  // Initialize the DataTable for this selector
  const boot = () => dtFactory.initDataTable(tableSelector, requestedApiUrl);

  // Handle DOM ready state
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", boot, { once: true });
  } else {
    boot();
  }
}
